import { useCallback, useEffect, useId, useState } from "react";
import { Plus, Shield, Trash2 } from "lucide-react";

import type { CustomHtmlAttribute } from "../style-state-system";

interface CustomHtmlAttributesProps {
  readonly value: readonly CustomHtmlAttribute[] | undefined;
  readonly onChange: (attributes: readonly CustomHtmlAttribute[]) => void;
}

const EMPTY_ATTRIBUTE: CustomHtmlAttribute = { name: "", value: "" };

export default function CustomHtmlAttributes({
  value,
  onChange,
}: CustomHtmlAttributesProps) {
  const baseId = useId();
  const canonicalAttributes = value ?? [];
  const [draftAttributes, setDraftAttributes] = useState<readonly CustomHtmlAttribute[]>(
    canonicalAttributes,
  );

  useEffect(() => {
    if (canonicalAttributes.length > 0 || draftAttributes.length === 0) {
      setDraftAttributes(canonicalAttributes);
    }
  }, [canonicalAttributes, draftAttributes.length]);

  const updateAttribute = useCallback(
    (index: number, patch: Partial<CustomHtmlAttribute>) => {
      const nextAttributes = draftAttributes.map((attribute, currentIndex) => (
        currentIndex === index ? { ...attribute, ...patch } : attribute
      ));
      setDraftAttributes(nextAttributes);
      onChange(nextAttributes);
    },
    [draftAttributes, onChange],
  );

  const addAttribute = useCallback(() => {
    const nextAttributes = [...draftAttributes, EMPTY_ATTRIBUTE];
    setDraftAttributes(nextAttributes);
  }, [draftAttributes]);

  const removeAttribute = useCallback(
    (index: number) => {
      const nextAttributes = draftAttributes.filter((_, currentIndex) => currentIndex !== index);
      setDraftAttributes(nextAttributes);
      onChange(nextAttributes);
    },
    [draftAttributes, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Shield className="h-3.5 w-3.5" />
        Custom HTML Attributes
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-3">
        <p className="m-0 text-[11px] leading-4 text-[var(--color-muted-foreground)]">
          Allowed only: <code>data-*</code>, <code>aria-*</code>, and <code>role</code>.
        </p>
      </div>

      {draftAttributes.length > 0 ? (
        <div className="flex flex-col gap-2" aria-label="Custom HTML attributes">
          {draftAttributes.map((attribute, index) => (
            <div
              key={`${baseId}-${index}`}
              className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2"
            >
              <input
                type="text"
                value={attribute.name}
                onChange={(event) => updateAttribute(index, { name: event.target.value })}
                placeholder="data-testid"
                className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
                aria-label={`Attribute name ${index + 1}`}
              />
              <input
                type="text"
                value={attribute.value}
                onChange={(event) => updateAttribute(index, { value: event.target.value })}
                placeholder="hero-cta"
                className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
                aria-label={`Attribute value ${index + 1}`}
              />
              <button
                type="button"
                onClick={() => removeAttribute(index)}
                className="inline-flex h-7 w-7 items-center justify-center rounded border border-[var(--color-border)] text-[var(--color-muted-foreground)] transition-colors hover:border-[var(--color-destructive)] hover:text-[var(--color-destructive)]"
                aria-label={`Remove attribute ${index + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={addAttribute}
        className="inline-flex h-7 items-center justify-center gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)]/40 hover:text-[var(--color-primary)]"
      >
        <Plus className="h-3.5 w-3.5" />
        Add Attribute
      </button>
    </div>
  );
}
